/**
 * Client side of the campaign revision guard (PREC-4026).
 *
 * The API stamps `revision` on every accepted campaign write and refuses a write from an older
 * revision with `409 CAMPAIGN_STALE`. These helpers read that revision off a campaign entry, pick
 * the freshest of the two list copies the builder holds (socket snapshot vs. query cache), and
 * recognise the conflict answer as RTK Query surfaces it.
 */

export const CAMPAIGN_STALE_ERROR = "CAMPAIGN_STALE";

/** One campaign entry of `list.campaignConfig` as the payload carries it. */
export type StoredCampaignEntry = Record<string, unknown> & {
	revision?: number;
	isValid?: boolean;
	initialNodes?: unknown;
	initialEdges?: unknown;
};

export interface CampaignStaleConflict {
	campaignId: string;
	currentRevision: number;
	incomingRevision: number;
	/** The stored entry the server refused to overwrite. */
	current: StoredCampaignEntry | undefined;
}

type ListLike = { campaignConfig?: Record<string, unknown> | null };

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

/** A campaign saved before PREC-4026 carries no revision; it reads as 0. */
export function readCampaignRevision(entry: unknown): number {
	if (!isRecord(entry) || typeof entry.revision !== "number" || !Number.isFinite(entry.revision)) {
		return 0;
	}

	return Math.max(0, Math.floor(entry.revision));
}

/** Keep the preferred copy on equal revisions, otherwise use the newer acknowledgement. */
export function pickFreshestCampaignEntry(
	preferred: StoredCampaignEntry | undefined,
	acknowledged: StoredCampaignEntry | undefined
): StoredCampaignEntry | undefined {
	return readCampaignRevision(acknowledged) > readCampaignRevision(preferred)
		? acknowledged
		: preferred;
}

export function readStoredCampaign(
	list: ListLike | undefined,
	campaignUuid: string
): StoredCampaignEntry | undefined {
	const entry = list?.campaignConfig?.[campaignUuid];

	return isRecord(entry) ? (entry as StoredCampaignEntry) : undefined;
}

/**
 * The builder used to read "socket copy, else cache" — but the socket copy is refreshed only when
 * the campaign it watches is saved, and the cache only after this tab's own saves, so either can be
 * hours older than the other. Take whichever carries the higher revision for this campaign; on a
 * tie keep the socket copy, as before.
 */
export function pickFreshestListData<T extends ListLike>(
	socketList: T | undefined,
	cacheList: T | undefined,
	campaignUuid: string
): T | undefined {
	const socketEntry = readStoredCampaign(socketList, campaignUuid);
	const cacheEntry = readStoredCampaign(cacheList, campaignUuid);

	if (!socketList || (!socketEntry && cacheEntry)) {
		return cacheList ?? socketList;
	}

	if (!cacheList || !cacheEntry) {
		return socketList;
	}

	return readCampaignRevision(cacheEntry) > readCampaignRevision(socketEntry)
		? cacheList
		: socketList;
}

/** The `409 CAMPAIGN_STALE` answer, or null for any other rejection. */
export function readCampaignStaleConflict(error: unknown): CampaignStaleConflict | null {
	if (!isRecord(error) || error.status !== 409 || !isRecord(error.data)) {
		return null;
	}

	const { message, campaignId, currentRevision, incomingRevision, current } = error.data;

	if (message !== CAMPAIGN_STALE_ERROR || typeof campaignId !== "string") {
		return null;
	}

	return {
		campaignId,
		currentRevision: readCampaignRevision({ revision: currentRevision }),
		incomingRevision: readCampaignRevision({ revision: incomingRevision }),
		current: isRecord(current) ? (current as StoredCampaignEntry) : undefined,
	};
}

/** The API's code for a write that would restructure a campaign which has sent (PREC-4616). */
export const CAMPAIGN_STRUCTURE_LOCKED_ERROR = "CAMPAIGN_STRUCTURE_LOCKED";

/** Whether a rejection is the structure lock refusing a change to this campaign's steps. */
export function isCampaignStructureLockedRefusal(error: unknown, campaignUuid: string): boolean {
	return (
		isRecord(error) &&
		error.status === 409 &&
		isRecord(error.data) &&
		error.data.code === CAMPAIGN_STRUCTURE_LOCKED_ERROR &&
		error.data.campaignId === campaignUuid
	);
}

/** The API's code for a write to a campaign that was deleted (PREC-4258). */
export const CAMPAIGN_DELETED_ERROR = "CAMPAIGN_DELETED";

/** Whether a rejection is the API refusing a write because this campaign was deleted. */
export function isCampaignDeletedRefusal(error: unknown, campaignUuid: string): boolean {
	return (
		isRecord(error) &&
		error.status === 409 &&
		isRecord(error.data) &&
		error.data.code === CAMPAIGN_DELETED_ERROR &&
		error.data.campaignId === campaignUuid
	);
}
